import * as React from 'react'

import { Box, TagColor } from '@factorialco/deprecated-design-system'
import _ from 'lodash'

import { Tabs } from 'modules/core/components/Table'
import type { TabType } from 'modules/core/components/Table'
import type { WithMultiSelectStateChildrenProps } from 'modules/core/components/WithMultiSelectState'
import i18n from 'modules/core/lib/i18n'

import type { ImportedEmployee } from 'modules/employees/lib/ImportedEmployee'
import ImportButton from 'modules/employees/pages/ImportEmployeesPage/components/ImportButton'

import EmployeesTable from '../EmployeesTable'

type WithMultiSelectProps = WithMultiSelectStateChildrenProps<ImportedEmployee>

interface Props {
  employees: ImportedEmployee[]
  selected: ImportedEmployee[]
  attributes: string[]
  customFields: string[]
  onToggleSelectedItem: WithMultiSelectProps['onToggleSelectedItem']
  onDeselectAll: () => void
  onSelectAll: () => void
  toggleModal: () => void
}

const TableTabs = ({
  employees,
  selected,
  attributes,
  customFields,
  onToggleSelectedItem,
  onDeselectAll,
  onSelectAll,
  toggleModal,
}: Props) => {
  const employeeStatus = (employee: ImportedEmployee): string => {
    return employee.status
  }

  const employeesByStatus = {
    ready: [],
    conflicts: [],
    errored: [],
    // This happens because `status` is defined as a `string` on
    // `ImportedEmployee` if it defined the values it can have we could just
    // let flow do its magic here because it would know in advance the values
    // of the keys it would be able to generate.
    //
    ..._.groupBy(employees, employeeStatus),
  }

  const conflictsTooltip = i18n.t('import.tabs.tooltips.conflicts', {
    count: employeesByStatus.conflicts.length,
  })

  const erroredTooltip = i18n.t('import.tabs.tooltips.errored', {
    count: employeesByStatus.errored.length,
  })

  const tabs: Array<TabType<'ready' | 'conflicts' | 'errored'>> = [
    {
      id: 'ready',
      counter: employeesByStatus.ready.length,
      label: i18n.t('import.tabs.ready'),
      color: TagColor.brand,
      tooltipColor: 'brand',
    },
    {
      id: 'conflicts',
      counter: employeesByStatus.conflicts.length,
      label: i18n.t('import.tabs.conflicts'),
      tooltip: employeesByStatus.conflicts.length ? conflictsTooltip : '',
      color: TagColor.accent,
      tooltipColor: 'accent',
    },
    {
      id: 'errored',
      counter: employeesByStatus.errored.length,
      label: i18n.t('import.tabs.errored'),
      tooltip: employeesByStatus.errored.length ? erroredTooltip : '',
      color: TagColor.wrong,
      tooltipColor: 'wrong',
    },
  ]

  return (
    <Box style={{ position: 'relative' }}>
      <Box style={{ position: 'absolute', right: '0px' }}>
        <ImportButton selectedEmployees={selected} onClickImport={toggleModal} />
      </Box>
      <Tabs tabs={tabs}>
        {({ tab, isSelected }) => (
          <Tabs.Panel tab={tab} isSelected={isSelected} key={tab.id}>
            <EmployeesTable
              tabId={tab.id}
              employees={employeesByStatus[tab.id]}
              selected={selected}
              attributes={attributes}
              customFields={customFields}
              onToggleSelectedItem={onToggleSelectedItem}
              onSelectAll={onSelectAll}
              onDeselectAll={onDeselectAll}
            />
          </Tabs.Panel>
        )}
      </Tabs>
    </Box>
  )
}

export default TableTabs
